import React from 'react';
import { motion } from 'framer-motion';
import { useNavigationStore } from '../stores/navigationStore';
import { useProgressStore } from '../stores/progressStore';
import type { BlogPost } from '../types';

interface BlogCardProps {
  post: BlogPost;
  onClick: (post: BlogPost) => void;
}

const BlogCard: React.FC<BlogCardProps> = ({ post, onClick }) => {
  const { isMobile } = useNavigationStore();
  const { isUnlocked } = useProgressStore();

  const handleClick = () => {
    onClick({
      ...post,
      visualization: typeof post.visualization === 'string' 
        ? post.visualization 
        : Array.isArray(post.visualization) 
          ? post.visualization[0] 
          : undefined
    });
  };

  const cardContent = (
    <>
      {post.imageName && (
        <div
          className="h-32 md:h-40 w-full bg-cover bg-center rounded-t-xl"
          style={{ backgroundImage: `url(/assets/media/blog/${post.imageName})` }}
        />
      )}
      <div className="p-3 md:p-4">
        <h2 className="text-base md:text-lg font-bold text-white mb-1 line-clamp-2">{post.title}</h2>
        <p className="text-gray-400 text-xs mb-1 md:mb-2">{post.date}</p>
        <p className="text-gray-300 text-xs md:text-sm mb-2 md:mb-3 line-clamp-3">{post.summary}</p>
        <div className="flex flex-wrap gap-1">
          {post.tags?.map(tag => (
            <span
              key={tag}
              className="bg-blue-900/40 text-blue-300 text-xs px-2 py-0.5 rounded-full"
            >
              {tag}
            </span>
          ))}
        </div>
      </div>
    </>
  );

  if (isMobile) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        onClick={handleClick}
        className="relative bg-black/30 backdrop-blur-sm rounded-xl overflow-hidden cursor-pointer border border-gray-800 hover:border-gray-700 transition-colors mb-2"
      >
        {cardContent}
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      onClick={handleClick}
      className="group relative bg-black/30 backdrop-blur-sm rounded-xl overflow-hidden cursor-pointer border border-gray-800 hover:border-gray-700 transition-all duration-300 hover:shadow-xl hover:shadow-blue-500/10"
    >
      {cardContent}
    </motion.div>
  );
};

export default BlogCard;
